import { Tracking, CreateCaseNoteResponse, TrackingEvent, TrackingDetails } from '../../services/api';
import { P2GTranslation } from '@parcel2go/peacock-legacy-resources/dist/translations';
import { ChatContext } from '@parcel2go/peacock-legacy-chat/dist/chat';
import { P2GGrid } from '@parcel2go/peacock-legacy-core/dist/grid';
import { P2GText } from '@parcel2go/peacock-legacy-core/dist/typography';
import React from 'react';
import TrackingIssues, { TrackingIssueMessage } from '../issues';
import TrackingCase from '../case-details';
import { P2GLink } from '@parcel2go/peacock-legacy-core/dist/link';
import {
  GetLastTrackingEventTimeStamp,
  GetLastTrackingEventTimeStampSingleParcel,
  GetRelativeTimestampByDays,
  HasEstimatedDeliveryDatePassed
} from "../../services/helpers";
import TrackingWindow from '../window';
import { P2GBox } from '@parcel2go/peacock-legacy-core';
import { hasDeliveryIssue } from '../utils/mapParcelStage'
import makeStyles from '@material-ui/core/styles/makeStyles';

const TrackingCollection = React.lazy(() => import('../../components/collection/index'));

interface ITrackingNotifications {
  tracking: Tracking,
  onLoadChatContext: (profile: string) => Promise<ChatContext>
  onChatStarted: () => void,
  onChatEnded: (start?: Date | null, end?: Date | null) => void,
  onOpenValidation: () => void,
  onSendReply: (caseId: string, note: string) => Promise<CreateCaseNoteResponse>,
  withWindow?: TrackingEvent | null
  parcelNumbers: string[]
  isPerParcelNotification?: boolean
  currentParcel?: TrackingDetails
}

const useStyles = makeStyles(() => ({
  textSection: {
    flexDirection: 'column',
    justifyDirection: 'flex-start',
    alignItems: 'stretch',
    marginBottom: '8px'
  }
}))

const TrackingNotifications: React.FC<ITrackingNotifications> = (props) => {
  const classes = useStyles();
  const {tracking, withWindow, currentParcel, parcelNumbers, isPerParcelNotification } = props;

  const estimatedDeliveryHasPassed = HasEstimatedDeliveryDatePassed(tracking);

  if(parcelNumbers.length <= 1 && !isPerParcelNotification){
    const lastTrackingEventScan = GetLastTrackingEventTimeStamp(tracking);

    const isNotTrackingInTransit = lastTrackingEventScan ? 
    !tracking.complete &&
    !estimatedDeliveryHasPassed &&
    !tracking.hasRTSParcel && 
    !tracking.hasHeldInCustomsParcel &&
    lastTrackingEventScan.getTime() < GetRelativeTimestampByDays(4) 
    : false;

    if(tracking.isOwner && tracking.case !== null){
      return (
        <>
          {tracking.order.foundOrder && withWindow?.type !== "Collection Scheduled" &&
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingCase caseDetails={tracking.case} onSendReply={props.onSendReply} />
            </P2GGrid>
          }
        </>
      )
    }

    if(!tracking.isOwner && tracking.case){
      return(
        <P2GGrid item container classNames={classes.textSection}>
          <TrackingIssues
            tracking={tracking}
            onOpenValidation={props.onOpenValidation}
            loadChatContext={props.onLoadChatContext}
            onChatStarted={props.onChatStarted}
            onChatEnded={props.onChatEnded}
            headingVariant='h2'
            courierName={tracking.order.service.courierName}
          />
        </P2GGrid>
      )
    }

    return (
      <>
        {!tracking.complete && !withWindow && !(tracking.hasIssues || tracking.isLate || tracking.possiablyLost || isNotTrackingInTransit) && tracking.started &&
          <P2GGrid item container classNames={classes.textSection}>
            <P2GBox pt={2} pb={2}>
              <P2GText variant="h2" component='h2'>
                  <P2GTranslation translation="TRACKING.HISTORY.COURIER" options={{ name: tracking.order.service.courierName }}>
                      {`Your ${tracking.order.service.courierName} parcel is on it's way!`}
                  </P2GTranslation>
              </P2GText>
              <P2GText variant="body1" component='p'>
                  <P2GTranslation translation="TRACKING.HISTORY.SERVICELEVELAGREEMENT">
                      {`Please allow for 24hrs for the tracking to update.`}
                  </P2GTranslation>   
              </P2GText>
            </P2GBox>
          </P2GGrid>
        }

        {!tracking.complete && withWindow &&
          <P2GGrid item>
            <TrackingWindow event={withWindow} started={tracking.started} headingVariant="h2" />
          </P2GGrid>
        }

        {tracking.complete && tracking.case === null && !tracking.hasRTSParcel && withWindow?.type !== "Collection Scheduled" &&
          <P2GGrid item container classNames={classes.textSection}>
            <P2GText variant="h2"><P2GTranslation translation="TRACKING.RESULT.DELIVERED.TITLE"></P2GTranslation></P2GText>
            <P2GText variant="body1" component='p'>
              <P2GTranslation translation="TRACKING.RESULT.DELIVERED.DESCRIPTION" components={[<P2GLink href="/service-centre/select/choose" />]}>
                TRACKING.RESULT.DELIVERED.DESCRIPTION
              </P2GTranslation>
            </P2GText>
          </P2GGrid>
        }

        {tracking.complete && tracking.case === null && tracking.hasRTSParcel && withWindow?.type !== "Collection Scheduled" &&
          <P2GGrid item container classNames={classes.textSection}>
            <TrackingIssueMessage
              orderReference={tracking.order.orderLineId}
              color="error"
              title="TRACKING.RESULT.RTSDELIVERED.TITLE"
              message="TRACKING.RESULT.RTSDELIVERED.DESCRIPTION"
              showChat={false}
              components={[<P2GLink href="/service-centre/select/choose" />]}
              showContact={false} headingVariant="h2"
              showOptions={true}
              onValidateOrder={props.onOpenValidation}
              loadChatContext={props.onLoadChatContext}
              onChatStarted={props.onChatStarted}
              onChatEnded={props.onChatEnded}>
              Your parcel has been delivered back to the sender
            </TrackingIssueMessage>
          </P2GGrid>
        }

        {tracking.order.foundOrder && !tracking.started && !tracking.order.service.isDropOff && withWindow?.type !== "Collection Scheduled" &&
          <P2GGrid item container classNames={classes.textSection}>
            <TrackingCollection
              hasAddressIssue={tracking.hasAddressIssueParcel}
              hasAttemptedCollection={tracking.hasAttemptedCollectionParcel}
              hasCancelled={tracking.hasCancelledParcel}
              isOwner={tracking.isOwner}
              orderReference={tracking.order.orderLineId}
              loadChatContext={props.onLoadChatContext}
              onChatStarted={props.onChatStarted}
              onChatEnded={props.onChatEnded}
              headingVariant='h2'
            />
          </P2GGrid>
        }

        {tracking.order.foundOrder && !tracking.started && tracking.order.service.isDropOff && withWindow?.type !== "Collection Scheduled" &&
          <P2GGrid item container classNames={classes.textSection}>
            <P2GText variant="h2"><P2GTranslation translation="TRACKING.RESULT.DROPOFF.TITLE"></P2GTranslation></P2GText>
            <P2GText variant="body1" component='p'>
              <P2GTranslation translation="TRACKING.RESULT.DROPOFF.DESCRIPTION"></P2GTranslation>
            </P2GText>
          </P2GGrid>
        }

        {tracking.started && tracking.order.foundOrder && (tracking.hasIssues || tracking.isLate || tracking.possiablyLost || isNotTrackingInTransit) && withWindow?.type !== "Collection Scheduled" &&
          <P2GGrid item container classNames={classes.textSection}>
            <TrackingIssues
              tracking={tracking}
              onOpenValidation={props.onOpenValidation}
              loadChatContext={props.onLoadChatContext}
              onChatStarted={props.onChatStarted}
              onChatEnded={props.onChatEnded}
              headingVariant='h2'
              courierName={tracking.order.service.courierName}
            />
          </P2GGrid>
        }
      </>
    )
  }

  if(parcelNumbers.length > 1 && !isPerParcelNotification){

    if(tracking.isOwner && tracking.case !== null){
      return (
        <>
          {tracking.order.foundOrder && withWindow?.type !== "Collection Scheduled" &&
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingCase caseDetails={tracking.case} onSendReply={props.onSendReply} />
            </P2GGrid>
          }
        </>
      )
    }

    if(!tracking.isOwner && tracking.case){
      return(
        <P2GGrid item container classNames={classes.textSection}>
          <TrackingIssues
            tracking={tracking}
            onOpenValidation={props.onOpenValidation}
            loadChatContext={props.onLoadChatContext}
            onChatStarted={props.onChatStarted}
            onChatEnded={props.onChatEnded}
            headingVariant='h2'
            courierName={tracking.order.service.courierName}
          />
        </P2GGrid>
      )
    }

    if (tracking.order.foundOrder && !tracking.started && tracking.order.service.isDropOff && withWindow?.type !== "Collection Scheduled"){
      return (
          <P2GGrid item container classNames={classes.textSection}>
            <P2GText variant="h2"><P2GTranslation translation="TRACKING.RESULT.DROPOFF.TITLE"></P2GTranslation></P2GText>
            <P2GText variant="body1" component='p'>
              <P2GTranslation translation="TRACKING.RESULT.DROPOFF.DESCRIPTION"></P2GTranslation>
            </P2GText>
          </P2GGrid>
      )
    }

    return (
      <>
        {!tracking.case && 
          <P2GGrid item container classNames={classes.textSection}>
            <P2GBox pt={2} pb={2}>
              <P2GText variant="h2" component='h2'>
                  <P2GTranslation translation="TRACKING.HISTORY.OVERVIEW.MULTIPARCEL.TITLE" options={{ count: parcelNumbers.length }}>
                      {`You have ${parcelNumbers.length} parcels with this order.`}
                  </P2GTranslation>
              </P2GText>
              <P2GText variant="body1" component='p'>
                  <P2GTranslation translation="TRACKING.HISTORY.OVERVIEW.MULTIPARCEL.SUBTITLE">
                      {`To view tracking on each parcel go to tracking history.`}
                  </P2GTranslation>   
              </P2GText>
            </P2GBox>
          </P2GGrid>
        }
      </>
    )
  }

  if(parcelNumbers.length > 1 && isPerParcelNotification && currentParcel){
    const lastTrackingEventScanCurrentParcel = GetLastTrackingEventTimeStampSingleParcel(currentParcel);
    const hasIssues = hasDeliveryIssue(currentParcel)

    const isNotTrackingInTransitCurrentParcel = lastTrackingEventScanCurrentParcel ? 
    !currentParcel.delivered &&
    !estimatedDeliveryHasPassed &&
    !currentParcel.isRTS &&
    !currentParcel.isHeldInCustoms &&
    lastTrackingEventScanCurrentParcel.getTime() < GetRelativeTimestampByDays(4) 
    : false;

    const isAFailedDeliveryAttempt = currentParcel.addressIssue || currentParcel.collectionAttempted || currentParcel.isCancelled ? true : false;
    
    return (
      <>
        {!currentParcel.delivered && !withWindow && !(hasIssues || currentParcel.isDelayed || currentParcel.isLost || isNotTrackingInTransitCurrentParcel) && tracking.started &&
          <P2GGrid item container classNames={classes.textSection}>
            <P2GText variant="h5" component='p'>
                <P2GTranslation translation="TRACKING.HISTORY.COURIER" options={{ name: tracking.order.service.courierName }}>
                    {`Your ${tracking.order.service.courierName} parcel is on it's way!`}
                </P2GTranslation>
            </P2GText>
            <P2GText variant="body1" component='p'>
                <P2GTranslation translation="TRACKING.HISTORY.SERVICELEVELAGREEMENT">
                    {`Please allow for 24hrs for the tracking to update.`}
                </P2GTranslation>   
            </P2GText>
          </P2GGrid>
        }

        {!currentParcel.delivered && withWindow &&
          <>
            <P2GGrid item>
              <TrackingWindow event={withWindow} started={tracking.started} headingVariant="h5"/>
            </P2GGrid>
          </>
        }

        {currentParcel.delivered && !currentParcel.isRTS && withWindow?.type !== "Collection Scheduled" && !isAFailedDeliveryAttempt &&
          <P2GGrid item container classNames={classes.textSection}>
            <P2GText variant="h5"><P2GTranslation translation="TRACKING.RESULT.DELIVERED.TITLE"></P2GTranslation></P2GText>
            <P2GText variant="body1" component='p'>
              <P2GTranslation translation="TRACKING.RESULT.DELIVERED.DESCRIPTION" components={[<P2GLink href="/service-centre/select/choose" />]}>
                TRACKING.RESULT.DELIVERED.DESCRIPTION
              </P2GTranslation>
            </P2GText>
          </P2GGrid>
        }

        {currentParcel.delivered && currentParcel.isRTS && withWindow?.type !== "Collection Scheduled" &&
          <P2GGrid item container classNames={classes.textSection}>
            <TrackingIssueMessage
              orderReference={tracking.order.orderLineId}
              color="error"
              title="TRACKING.RESULT.RTSDELIVERED.TITLE"
              message="TRACKING.RESULT.RTSDELIVERED.DESCRIPTION"
              showChat={false}
              components={[<P2GLink href="/service-centre/select/choose" />]}
              showContact={false} headingVariant="h2"
              showOptions={true}
              onValidateOrder={props.onOpenValidation}
              loadChatContext={props.onLoadChatContext}
              onChatStarted={props.onChatStarted}
              onChatEnded={props.onChatEnded}>
              Your parcel has been delivered back to the sender
            </TrackingIssueMessage>
          </P2GGrid>
        }

        {tracking.order.foundOrder && !tracking.started && !tracking.order.service.isDropOff && withWindow?.type !== "Collection Scheduled" && isAFailedDeliveryAttempt &&
          <P2GGrid item container classNames={classes.textSection}>
            <TrackingCollection
              hasAddressIssue={currentParcel.addressIssue}
              hasAttemptedCollection={currentParcel.collectionAttempted}
              hasCancelled={currentParcel.isCancelled}
              isOwner={tracking.isOwner}
              orderReference={tracking.order.orderLineId}
              loadChatContext={props.onLoadChatContext}
              onChatStarted={props.onChatStarted}
              onChatEnded={props.onChatEnded}
              headingVariant='h5'
            />
          </P2GGrid>
        }

        {tracking.order.foundOrder && !tracking.started && tracking.order.service.isDropOff && withWindow?.type !== "Collection Scheduled" &&
          <P2GGrid item container classNames={classes.textSection}>
            <P2GText variant="h5"><P2GTranslation translation="TRACKING.RESULT.DROPOFF.TITLE"></P2GTranslation></P2GText>
            <P2GText variant="body1" component='p'>
              <P2GTranslation translation="TRACKING.RESULT.DROPOFF.DESCRIPTION"></P2GTranslation>
            </P2GText>
          </P2GGrid>
        }

        {tracking.started && tracking.order.foundOrder 
          && (hasIssues || currentParcel.isDelayed || currentParcel.isLost || isNotTrackingInTransitCurrentParcel) 
          && withWindow?.type !== "Collection Scheduled" 
          && !currentParcel.delivered &&
          <P2GGrid item container classNames={classes.textSection}>
            <TrackingIssues
              tracking={tracking}
              onOpenValidation={props.onOpenValidation}
              loadChatContext={props.onLoadChatContext}
              onChatStarted={props.onChatStarted}
              onChatEnded={props.onChatEnded}
              headingVariant='h5'
              courierName={tracking.order.service.courierName}
            />
          </P2GGrid>
        }

      </>);
  }

  return null;
}

export default TrackingNotifications;
